import { inject, Injectable } from "@angular/core";
import { ErrorReportingService } from "@utils/error-reporting.service";
import { CONFIG_TOKEN, INDEXED_DB, NgxIndexedDBService, WithID } from "ngx-indexed-db";
import { firstValueFrom, Observable } from "rxjs";
import { defaultIfEmpty, timeout } from "rxjs/operators";

const OPERATION_TIMEOUT_MS = 10000;

@Injectable()
export class ResilientIndexedDbService extends NgxIndexedDBService {
  private errorReportingService = inject(ErrorReportingService);

  /** Tail of the serialization chain. Never rejects - a failed operation must not break the queue. */
  private queue: Promise<void> = Promise.resolve();

  constructor() {
    super(inject(CONFIG_TOKEN), inject(INDEXED_DB));
  }

  override getAll<T>(storeName: string): Observable<T[]> {
    return this.enqueue(() => super.getAll<T>(storeName), "getAll", storeName);
  }

  override getByID<T>(storeName: string, id: string | number): Observable<T> {
    return this.enqueue(() => super.getByID<T>(storeName, id), "getByID", storeName);
  }

  override getByKey<T>(storeName: string, key: IDBValidKey): Observable<T> {
    return this.enqueue(() => super.getByKey<T>(storeName, key), "getByKey", storeName);
  }

  override add<T>(storeName: string, value: T, key?: unknown): Observable<T & WithID> {
    return this.enqueue(() => super.add<T>(storeName, value, key), "add", storeName);
  }

  override update<T>(storeName: string, value: T): Observable<T> {
    return this.enqueue(() => super.update<T>(storeName, value), "update", storeName);
  }

  override deleteByKey(storeName: string, query: IDBValidKey | IDBKeyRange): Observable<void> {
    return this.enqueue(() => super.deleteByKey(storeName, query), "deleteByKey", storeName);
  }

  override bulkPut<T>(storeName: string, items: T[]): Observable<IDBValidKey> {
    return this.enqueue(() => super.bulkPut<T>(storeName, items), "bulkPut", storeName);
  }

  override clear(storeName: string): Observable<void> {
    return this.enqueue(() => super.clear(storeName), "clear", storeName);
  }

  /**
   * Runs `factory` only after every previously enqueued operation has settled (success, failure, or
   * timeout), and never runs two operations at the same time. A failing or timed out operation is
   * reported and surfaced to the caller as an error, but the queue keeps processing afterwards.
   */
  private enqueue<T>(factory: () => Observable<T>, operation: string, storeName: string): Observable<T> {
    return new Observable<T>((subscriber) => {
      let cancelled = false;

      this.queue = this.queue.then(async () => {
        if (cancelled) {
          return;
        }

        try {
          const value = await firstValueFrom(
            factory().pipe(timeout({ first: OPERATION_TIMEOUT_MS }), defaultIfEmpty(undefined as T)),
          );

          if (!cancelled) {
            subscriber.next(value);
            subscriber.complete();
          }
        } catch (error) {
          this.errorReportingService.captureException(error, {
            tags: { area: "indexed-db" },
            extra: { operation, storeName },
          });

          if (!cancelled) {
            subscriber.error(error);
          }
        }
      });

      return () => {
        cancelled = true;
      };
    });
  }
}
